import { useCallback, useMemo } from 'react';

import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { useTierGroupProcessor } from 'pages/loyalty/loyalty-offers/tiered-offers/use-tier-group-processor';
import { mapOffersToTierGroup } from 'pages/loyalty/loyalty-offers/tiered-offers/utils';
import { selectors, useAppSelector } from 'state/global-state';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { LoyaltyAppliedOffer, LoyaltyOffer } from 'state/loyalty/types';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';

const hasBlockingErrors = (errorCode: IncentiveEvaluationErrorCodes): boolean =>
  !FixableEvaluationErrors.has(errorCode);

const getOfferId = (
  offer: LoyaltyAppliedOffer | LoyaltyOffer | null | undefined
): string | undefined | null => {
  if (!offer) {
    return null;
  }
  if ('loyaltyEngineId' in offer) {
    return offer.loyaltyEngineId;
  } else if ('id' in offer) {
    return offer.id;
  }
  return null;
};

export function useLoyaltyOfferAvailabilityEvaluation() {
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const { processTierGroup } = useTierGroupProcessor();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);

  const cmsOffersMap = useMemo(() => getCmsOffersMapByCmsId(cmsOffers), [cmsOffers]);
  const offersToTierGroupMap = useMemo(() => mapOffersToTierGroup(cmsOffers), [cmsOffers]);

  const findCmsOfferByCmsId = useCallback(
    (cmsId: string) => {
      return cmsOffersMap[cmsId] || null;
    },
    [cmsOffersMap]
  );

  const isOfferUnavailable = useCallback(
    (offer: LoyaltyAppliedOffer | LoyaltyOffer | null | undefined) => {
      const offerId = getOfferId(offer);
      if (!offerId) {
        return true;
      }

      const tierGroup = offersToTierGroupMap[offerId];
      if (tierGroup) {
        const { selectedOffer } = processTierGroup(tierGroup);
        if (!selectedOffer || selectedOffer.loyaltyEngineId !== offerId) {
          // The offer belongs to a Tier Group but is not the selected offer or
          // there is no available tier for this store
          return true;
        }
      }

      const selectedCMSOffer = 'cmsId' in offer && findCmsOfferByCmsId(offer.cmsId);

      const isSelectedLoyaltyOfferAvailable = selectedCMSOffer
        ? checkLoyaltyOfferAvailability(selectedCMSOffer)
        : false;

      const errorCodes = offersFeedbackMap[offerId];
      const someErrorIsNotFixable = errorCodes?.some(error =>
        hasBlockingErrors(error?.code as IncentiveEvaluationErrorCodes)
      );

      const isUnavailable = Boolean(
        (selectedCMSOffer && !isSelectedLoyaltyOfferAvailable) ||
          (errorCodes && someErrorIsNotFixable)
      );

      return isUnavailable;
    },
    [
      checkLoyaltyOfferAvailability,
      offersFeedbackMap,
      processTierGroup,
      findCmsOfferByCmsId,
      offersToTierGroupMap,
    ]
  );

  return { isOfferUnavailable, findCmsOfferByCmsId };
}
